import React from 'react';
import { Button, Collapse, Text, useToast, Wrap } from '@cardboard-ui/react';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { groupBy } from 'utils/groupObjectsByProperty';
import { useAuthenticatedSession } from 'utils/sessionProvider';
import { MessageReactions_chat_messageFragment$key } from './__generated__/MessageReactions_chat_messageFragment.graphql';
import { MessageReactions_chat_createReaction_Mutation } from './__generated__/MessageReactions_chat_createReaction_Mutation.graphql';
import { MessageReactions_chat_removeReaction_Mutation } from './__generated__/MessageReactions_chat_removeReaction_Mutation.graphql';
import { ReactionTooltip } from './ReactionTooltip';
import { ReactionButton } from '../ReactionButton';
import { t } from '@lingui/macro';

const messageFragment = graphql`
  fragment MessageReactions_chat_messageFragment on Message {
    id
    ...ReactionButton_chat_messageFragment
    reactions {
      edges {
        node {
          id
          text
          senderId
          deletedAt
        }
      }
    }
  }
`;

const createReactionMutation = graphql`
  mutation MessageReactions_chat_createReaction_Mutation(
    $input: CreateReactionInput!
  ) {
    createReaction(input: $input) {
      id
      message {
        ...MessageReactions_chat_messageFragment
      }
    }
  }
`;

const removeReactionMutation = graphql`
  mutation MessageReactions_chat_removeReaction_Mutation($id: String!) {
    removeReaction(id: $id) {
      id
      message {
        ...MessageReactions_chat_messageFragment
      }
    }
  }
`;

interface MessageReactionsProps {
  message: MessageReactions_chat_messageFragment$key;
}

type Reaction = {
  id: string;
  text: unknown;
  senderId: string;
};

export const MessageReactions: React.FC<MessageReactionsProps> = ({
  message,
}) => {
  const { id, reactions, ...fragments } = useFragment(messageFragment, message);
  const { member: sessionMember } = useAuthenticatedSession();
  const toast = useToast();
  const onErrorToast = ({ action }: { action: 'add' | 'remove' }) =>
    toast({
      status: 'error',
      position: 'top-right',
      title:
        action === 'add'
          ? t`Could not add your reaction`
          : t`Could not remove that reaction`,
    });

  const reactionNodes = reactions.edges.map((e) => e.node);
  const hasReactions =
    reactionNodes.filter(({ deletedAt }) => !deletedAt).length > 0;

  const [createReaction] =
    useMutation<MessageReactions_chat_createReaction_Mutation>(
      createReactionMutation,
    );
  const [removeReaction] =
    useMutation<MessageReactions_chat_removeReaction_Mutation>(
      removeReactionMutation,
    );

  const handleReactionClick = (
    otherReaction: Reaction,
    myReaction?: Reaction,
  ) => {
    if (myReaction) {
      removeReaction({
        variables: {
          id: myReaction.id,
        },
        payloadErrorBehaviour: 'HANDLED',
        onError: (e) => {
          onErrorToast({ action: 'remove' });
        },
      });
      return;
    }

    createReaction({
      variables: {
        input: {
          messageId: id,
          reaction: otherReaction.text as string,
        },
      },
      payloadErrorBehaviour: 'HANDLED',
      onError: (e) => {
        onErrorToast({ action: 'add' });
      },
    });
  };

  const reactionsByEmoji = groupBy(reactionNodes, (n) => n.text as string);

  return (
    <Collapse in={hasReactions}>
      <Wrap spacing={1} data-qa="message-reactions">
        {Object.entries(reactionsByEmoji).map(([emoji, groupedReactions]) => {
          const groupedReactionsWithoutDeleted = groupedReactions.filter(
            (gr) => !gr.deletedAt,
          );

          const myReaction = groupedReactionsWithoutDeleted.find(
            (r) => r.senderId === sessionMember.id,
          );

          const memberIds = groupedReactionsWithoutDeleted.map(
            (r) => r.senderId as string,
          );

          if (!groupedReactionsWithoutDeleted.length) {
            return null;
          }

          return (
            <ReactionTooltip key={emoji} memberIds={memberIds} emoji={emoji}>
              <Button
                size="xs"
                variant="solid"
                colorScheme="gray"
                px={1}
                borderWidth={1.5}
                borderColor={!!myReaction ? 'blue.200' : 'transparent'}
                borderRadius="full"
                onClick={() =>
                  handleReactionClick(
                    groupedReactionsWithoutDeleted[0] as Reaction,
                    myReaction as Reaction,
                  )
                }
              >
                <Text fontSize="md">{emoji}</Text>
                <Text fontSize="xs" pl={1}>
                  {groupedReactionsWithoutDeleted.length}
                </Text>
              </Button>
            </ReactionTooltip>
          );
        })}
        <ReactionButton message={fragments} keepScrollAtTheBottom={() => {}} />
      </Wrap>
    </Collapse>
  );
};
